import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { fetchWithAuth } from "../api/fetchWithAuth";

const typeOptions = [
  { value: "", label: "-" },
  { value: "Functional", label: "Functional" },
  { value: "Non Functional", label: "Non Functional" },
  { value: "Info", label: "Info" },
  { value: "Performance", label: "Performance" }
];

const silOptions = [
  { value: "", label: "-" },
  { value: "0", label: "0" },
  { value: "1", label: "1" },
  { value: "2", label: "2" }
];

const classOptions = [
  { value: "", label: "-" },
  { value: "sys", label: "System" },
  { value: "sw", label: "Software" }
];

const statusOptions = [
  { value: "raw", label: "Raw" },
  { value: "review", label: "Review" },
  { value: "clarified", label: "Clarified" },
  { value: "needs_clarification", label: "Needs Clarification" },
  { value: "targeted", label: "Targeted" },
  { value: "implemented", label: "Implemented" }
];

const initialFormState = {
  applyNumber: false,
  number: "",
  applyNote: false,
  note: "",
  applyQuote: false,
  quote: "",
  applyType: false,
  type: "",
  applySil: false,
  sil: "",
  applyClass: false,
  class: "",
  applyStatus: false,
  status: "clarified",
  applyTarget: false,
  targetVersionId: ""
};

function RequirementBulkEditModal({ show, onClose, onSaved, selectedCount, selectedIds }) {
  const [form, setForm] = useState(initialFormState);
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    if (!show) return;

    setForm(initialFormState);

    const loadVersions = async () => {
      try {
        const res = await fetchWithAuth("/api/versions");

        if (!res.ok) {
          return;
        }

        const data = await res.json();
        setVersions(
          data
            .filter((version) => !version.closed)
            .sort((leftVersion, rightVersion) => {
              const activeCompare = Number(Boolean(rightVersion.isActive)) - Number(Boolean(leftVersion.isActive));
              if (activeCompare !== 0) return activeCompare;

              return String(leftVersion.name ?? "").localeCompare(
                String(rightVersion.name ?? ""),
                undefined,
                { sensitivity: "base", numeric: true }
              );
            })
        );
      } catch (err) {
        console.warn("loadVersions aborted:", err.message);
      }
    };

    loadVersions();
  }, [show]);

  const effectiveStatus = useMemo(() => {
    if (!form.applyStatus) return null;
    return form.status;
  }, [form.applyStatus, form.status]);

  const shouldShowTarget = (
    (form.applyStatus && (effectiveStatus === "targeted" || effectiveStatus === "implemented")) ||
    form.applyTarget
  );

  const handleToggle = (e) => {
    const { name, checked } = e.target;

    setForm((current) => {
      const next = {
        ...current,
        [name]: checked
      };

      if (name === "applyStatus" && !checked) {
        next.status = "clarified";
      }

      if (name === "applyTarget" && !checked && !next.applyStatus) {
        next.targetVersionId = "";
      }

      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((current) => {
      const next = {
        ...current,
        [name]: value
      };

      if (name === "status" && (value === "clarified" || value === "needs_clarification" || value === "review")) {
        if (!current.applyTarget) {
          next.targetVersionId = "";
        }
      }

      return next;
    });
  };

  const handleSubmit = async () => {
    if (
      !form.applyNumber &&
      !form.applyNote &&
      !form.applyQuote &&
      !form.applyType &&
      !form.applySil &&
      !form.applyClass &&
      !form.applyStatus &&
      !form.applyTarget
    ) {
      alert("Select at least one field to update");
      return;
    }

    const res = await fetchWithAuth("/api/requirements/bulk-update", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ids: selectedIds,
        updates: {
          number: form.applyNumber ? form.number : undefined,
          note: form.applyNote ? form.note : undefined,
          quote: form.applyQuote ? form.quote : undefined,
          type: form.applyType ? form.type : undefined,
          sil: form.applySil ? form.sil : undefined,
          class: form.applyClass ? form.class : undefined,
          status: form.applyStatus ? form.status : undefined,
          targetVersionId: form.applyTarget ? (form.targetVersionId ? Number(form.targetVersionId) : null) : undefined
        }
      })
    });

    if (!res.ok) {
      const error = await res.json().catch(() => null);
      alert(error?.error || "Bulk update failed");
      return;
    }

    onSaved();
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Bulk edit {selectedCount} requirements</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <div className="small text-muted mb-3">
            Target without status will automatically set status to Targeted.
          </div>
          <Form.Check
            className="mb-2"
            type="checkbox"
            label="Apply Number"
            name="applyNumber"
            checked={form.applyNumber}
            onChange={handleToggle}
          />
          {form.applyNumber && (
            <Form.Group className="mb-3">
              <Form.Control
                name="number"
                value={form.number}
                onChange={handleChange}
                placeholder="Number"
              />
            </Form.Group>
          )}

          <Form.Check
            className="mb-2"
            type="checkbox"
            label="Apply Note"
            name="applyNote"
            checked={form.applyNote}
            onChange={handleToggle}
          />
          {form.applyNote && (
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="note"
                value={form.note}
                onChange={handleChange}
                placeholder="Note"
              />
            </Form.Group>
          )}


          <Form.Check
            className="mb-2"
            type="checkbox"
            label="Apply Quote"
            name="applyQuote"
            checked={form.applyQuote}
            onChange={handleToggle}
          />
          {form.applyQuote && (
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={4}
                name="quote"
                value={form.quote}
                onChange={handleChange}
                placeholder="Quote"
              />
            </Form.Group>
          )}

          <Form.Check
            className="mb-2"
            type="checkbox"
            label="Apply Type"
            name="applyType"
            checked={form.applyType}
            onChange={handleToggle}
          />
          {form.applyType && (
            <Form.Group className="mb-3">
              <Form.Select
                name="type"
                value={form.type}
                onChange={handleChange}
              >
                {typeOptions.map((option) => (
                  <option key={option.value || "empty"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Check
            className="mb-2"
            type="checkbox"
            label="Apply SIL"
            name="applySil"
            checked={form.applySil}
            onChange={handleToggle}
          />
          {form.applySil && (
            <Form.Group className="mb-3">
              <Form.Select
                name="sil"
                value={form.sil}
                onChange={handleChange}
              >
                {silOptions.map((option) => (
                  <option key={option.value || "empty"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Check
            className="mb-2"
            type="checkbox"
            label="Apply Class"
            name="applyClass"
            checked={form.applyClass}
            onChange={handleToggle}
          />
          {form.applyClass && (
            <Form.Group className="mb-3">
              <Form.Select
                name="class"
                value={form.class}
                onChange={handleChange}
              >
                {classOptions.map((option) => (
                  <option key={option.value || "empty"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Check
            className="mb-2"
            type="checkbox"
            label="Apply Status"
            name="applyStatus"
            checked={form.applyStatus}
            onChange={handleToggle}
          />
          {form.applyStatus && (
            <Form.Group className="mb-3">
              <Form.Select
                name="status"
                value={form.status}
                onChange={handleChange}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Check
            className="mb-2"
            type="checkbox"
            label="Apply Target"
            name="applyTarget"
            checked={form.applyTarget}
            onChange={handleToggle}
          />
          {shouldShowTarget && (
            <Form.Group>
              <Form.Select
                name="targetVersionId"
                value={form.targetVersionId}
                onChange={handleChange}
              >
                <option value="">Select target version...</option>
                {versions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleSubmit}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RequirementBulkEditModal;
